// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import { cloneDeep, get, map, find, forEach, isNumber, isUndefined } from 'lodash';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles, Button } from '@material-ui/core';
import { FilterBySelect, FilterByText } from '../../../pageComponents/Filters';
import { format } from "date-fns";
import clsx from 'clsx';
import { fetchLockUserCreation } from '../../../actions/portalConfig';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import {
  Clear,
} from '@material-ui/icons/';
import {
  fetchRegistrationSettings,
} from "../../../actions/registrationSettings";
import {
  fetchOrganizationsFiltered,
  fetchUsers,
  resetApp,
  resetDeleteNotification,
  resetRevokeNotification,
  bulkDeleteUsers,
} from '../../../actions/user';
import {
  getConfig,
  getUserDetails,
  getLockUserCreation,
  getActiveUserContext,
  getPublicAuthSchemesList,
} from '../../../reducers/portalConfig';
import {
  getErrors,
  getIsError,
  getOrganizationListResults,
  getUserDeleteResult,
  getUserListResults,
  getUserListTotalElements,
  getUserRevokeAccessResult,
  getValidationError,
  getUserBulkDeleteResult,
} from '../../../reducers/user';
import {
  getRegistrationSettings,
} from "../../../reducers/registrationSettings";
import styles from './styles';
import ListContainer from '../../list';
import {
  FilterByName,
  FilterByUserName,
  FilterByEmail,
  FilterByOrganization,
  FilterByRole,
  FilterByStatus,
  FilterByAuthScheme,
  SortBy,
  FilterAndSortSeparator,
  Organization,
  Role,
} from './controls';
import {
  ALL,
  ALERT_SUCCESS,
  ALERT_ERROR,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  KEY_ENTER,
  SUCCESS,
  FAIL,
  USER_STATUS_LABEL,
  ROLE_PORTAL_ADMIN,
  ROLE_ORG_ADMIN,
  ROLE_ORG_PUBLISHER,
  USER_STATUS_REGISTRATION_INIT,
  USER_STATUS_REGISTRATION_PENDING_APPROVAL,
  USER_LIST_INACTIVITY_TYPE_FILTER_BY,
  IS_LESS_THAN,
  IS_GREATER_THAN,
  USER_BULK_DELETE_REQUEST,
} from '../../../constants';
import {
  hasPublisherRole,
  getCanListUsers,
  getCanManageUser,
  getUserRole,
} from '../../../utils/rbac';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import BulkDeleteUsers from './bulkDeleteUsers';

export const getUserEditLink = (user) => (
  getCanManageUser(user) && '/admin/console/users/details'
);

export const getFullName = ({ lastName = '', firstName = '' }) => (
  (lastName && firstName) ?
    `${lastName}, ${firstName}` :
    (lastName || firstName || getI18nFormattedMessage('label.user.no.name'))
);

const getStatusIcon = (inactiveDays, thresholdsSetting, classes) => {
  const {
    deleteInactiveUsersAfter,
    thresholdValues,
  } = thresholdsSetting;
  const daysToDelete = isNumber(inactiveDays) && (deleteInactiveUsersAfter - inactiveDays);
  const firstThreshold = (thresholdValues.length > 0) && thresholdValues[0];
  const secondThreshold = (thresholdValues.length > 1) && thresholdValues[1];
  const lastThreshold = (thresholdValues.length > 2) && thresholdValues[2];
  let colorClass = classes.statusLastThreshold;
  if (isUndefined(inactiveDays)) {
    colorClass = classes.statusInactive;
  } else if (!firstThreshold || (daysToDelete > firstThreshold)) {
    colorClass = classes.statusActive;
  } else if (!secondThreshold || (daysToDelete > secondThreshold)) {
    colorClass = classes.statusFirstThreshold;
  } else if (!lastThreshold || (daysToDelete > lastThreshold)) {
    colorClass = classes.statusSecondThreshold;
  }
  return (
    <FiberManualRecordIcon
      className={clsx(classes.statusIcon, colorClass)}
    />
  );
};

export const getUserListColumns = (authConfigNameMapParam, isPublisher,
  getI18nMessage, thresholdsSetting, classes) =>
  [
    {
      id: 'name',
      label: getI18nFormattedMessage('label.name'),
      minWidth: 100,
      link: getUserEditLink,
      value: getFullName,
    }, {
      id: 'username',
      label: getI18nFormattedMessage('label.username'),
      minWidth: 100,
      value: ({ authConfigUuid, status, username }) => {
        if (authConfigUuid === 'DEFAULT') {
          if ((status === USER_STATUS_REGISTRATION_INIT) ||
            (status === USER_STATUS_REGISTRATION_PENDING_APPROVAL)) {
              return getI18nMessage('label.user.no.username');
          }
          return username;
        }
        return username;
      },
    }, {
      id: 'email',
      label: getI18nFormattedMessage('label.email'),
      minWidth: 100,
    }, ...(isPublisher ? [{
      id: 'authConfigUuid',
      label: getI18nFormattedMessage('label.authscheme'),
      minWidth: 100,
      value: (params) => authConfigNameMapParam[params.authConfigUuid],
    }] : []), ...(isPublisher ? [{
      id: 'organization',
      label: getI18nFormattedMessage('label.organization(s)'),
      minWidth: 100,
      value: Organization,
    }] : []), {
      id: 'role',
      label: getI18nFormattedMessage('label.role'),
      minWidth: 100,
      value: Role,
    }, {
      id: 'status',
      label: getI18nFormattedMessage('label.status'),
      minWidth: 100,
      value: ({ status }) => (USER_STATUS_LABEL[status] || status),
    }, {
      id: 'Days Inactive',
      label: getI18nFormattedMessage('label.daysInactive'),
      minWidth: 100,
      value: ({ lastLoginTs }) => {
        if(!lastLoginTs) {
          const statusIcon = getStatusIcon(undefined, thresholdsSetting, classes);
          return <>{statusIcon}{getI18nFormattedMessage('label.not.applicable')}</>;
        }
        const today = moment();
        const lastLogin = moment(lastLoginTs);
        let inactiveDays = today.diff(lastLogin, 'days');
        if(inactiveDays < 0) {
          inactiveDays = 0;
        }
        const statusIcon = getStatusIcon(inactiveDays, thresholdsSetting, classes);
        return <>{statusIcon}{inactiveDays}</>;
      },
    },
  ];

export const UserList = (props) => {
  const {
    activeUserContext,
    classes,
    fetchRegistrationSettings,
    userContext,
    isError,
    userErrors = [],
    isLoading,
    totalElements = 0,
    results = [],
    lockUserCreation,
    organizationsList,
    authSchemesList,
    registrationSettings,
    userDeleteResult,
    userRevokeAccessResult,
    validationError,
    defaultSelectedRows = [],
    userBulkDeleteResult,
  } = props;

  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const SERVER_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXX"

  const [filterByName, setFilterByName] = useState('');
  const [filterByUserName, setFilterByUserName] = useState('');
  const [filterByEmail, setFilterByEmail] = useState('');
  const [filterByOrganization, setFilterByOrganization] = useState();
  const [filterByAuthScheme, setFilterByAuthScheme] = useState('ALL');
  const [filterByRole, setFilterByRole] = useState([]);
  const [filterByStatus, setFilterByStatus] = useState(ALL);
  const [sortBy, setSortBy] = useState('username,ASC');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [filterByDaysOfInactivity, setFilterByDaysOfInactivity] = useState('');
  const [appliedDaysOfInactivityFilter, setAppliedDaysOfInactivityFilter] = useState('');
  const [filterByInactivityType, setFilterByInactivityType] = useState(IS_GREATER_THAN);
  const [inactivityFilterList] = useState(USER_LIST_INACTIVITY_TYPE_FILTER_BY);
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const [filterByLastLoginFrom, setFilterByLastLoginFrom] = useState();
  const [filterByLastLoginTo, setFilterByLastLoginTo] = useState();
  const { thresholds = [],
    deleteInactiveUsersAfter } = registrationSettings || {};
  const thresholdsSetting = {
    deleteInactiveUsersAfter,
    thresholdValues: [...thresholds].reverse(),
  };
  const [selectedRows, setSelectedRows] = useState(defaultSelectedRows);

  const [nameSearchText, setNameSearchText] = useState('');
  const [usernameSearchText, setUserNameSearchText] = useState('');
  const [emailSearchText, setEmailSearchText] = useState('');
  const [orgSearchText, setOrgSearchText] = useState('');

  const [orgOptions, setOrgOptions] = useState([]);
  const [authConfigNameMap, setAuthConfigNameMap] = useState({});

  const isPublisher = hasPublisherRole(userContext);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const showUserDeleteSuccessMessage = () => {
    notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.deleted'));
    setTimeout(() => {
      props.resetDeleteNotification();
      notifyMessages('', '');
    }, 2500);
  };

  const showBulkUserDeleteSuccessMessage = () => {
    notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.bulk.deleted'));
    setTimeout(() => {
      props.resetDeleteNotification();
      notifyMessages('', '');
    }, 2500);
  };


  const showUserRevokeAccessSuccessMessage = () => {
    notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.revoked'));
    setTimeout(() => {
      props.resetRevokeNotification();
      notifyMessages('', '');
    }, 2500);
  };

  useEffect(() => {
    if(organizationsList.length > 0 ) {
      const orgOptions = map(organizationsList, item => ({ uuid: item.uuid, name: item.name }));
      setOrgOptions(orgOptions);
    }
  }, [organizationsList]);

  useEffect(() => {
    if (authSchemesList) {
      const authConfigMap = {};
      map(authSchemesList, authScheme => {
        const { authMethod, name, uuid } = authScheme;
        if (authMethod === 'DEFAULT') {
          authConfigMap['DEFAULT'] = name;
        } else {
          authConfigMap[uuid] = name;
        }
      })
      setAuthConfigNameMap(authConfigMap);
    }
  }, [authSchemesList]);

  const getUsersList = () => {
    const [by, order] = sortBy.split(',');
    props.fetchUsers({
      ...((filterByAuthScheme !== 'ALL') && { authConfigUuid: filterByAuthScheme }),
      name: filterByName,
      username: filterByUserName,
      email: filterByEmail,
      orgUuids: filterByOrganization,
      roleUuids: filterByRole,
      status: filterByStatus,
      lastLoginFrom: filterByLastLoginFrom,
      lastLoginTo: filterByLastLoginTo,
      sortBy: by,
      sortOrder: order,
      page,
      rowsPerPage,
    });
  };

  useEffect(() => {
    props.fetchLockUserCreation();
    fetchRegistrationSettings();
  }, []);

  useEffect(() => {
    if (userDeleteResult === SUCCESS) {
      showUserDeleteSuccessMessage();
    }
  }, [userDeleteResult]);

  useEffect(() => {
    if (userBulkDeleteResult === SUCCESS) {
      showBulkUserDeleteSuccessMessage();
      getUsersList();
      setSelectedRows([]);
    }
  }, [userBulkDeleteResult]);

  useEffect(() => {
    if (userRevokeAccessResult === SUCCESS) {
      showUserRevokeAccessSuccessMessage();
    }
  }, [userRevokeAccessResult]);

  useEffect(() => {
    if (!getCanListUsers(userContext)) {
      props.push('/404');
    } else {
      if (hasPublisherRole(userContext)) {
        props.fetchOrganizationsFiltered(50, 0, '');
      }
    }
  }, [userContext]);

  useEffect(() => {
    if (isError) {
      if (validationError) {
        notifyMessages(ALERT_ERROR, validationError);
      } else if (userBulkDeleteResult === FAIL) {
        notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.bulk.delete.error'));
      } else {
        notifyMessages(ALERT_ERROR, intl.getI18nMessage('error.user.fetch'));
      }
    }
  }, [isError, userErrors]);

  useEffect(() => {
    getUsersList();
    if (isInitialFetch) {
      setIsInitialFetch(false);
    } else {
      notifyMessages('','');
    }
  }, [
    filterByName, filterByUserName, filterByEmail, filterByOrganization,
    filterByAuthScheme, filterByRole, filterByStatus, filterByLastLoginFrom, filterByLastLoginTo,
    sortBy, page, rowsPerPage,
  ]);

  const onFilterByNameChange = (value) => {
    setNameSearchText(value);
  };
  const onNameSearchTextChanged = () => {
    setPage(0);
    setFilterByName(nameSearchText);
  };
  const onFilterByNameKeyPress = (e) => {
    if (e.key === KEY_ENTER) {
      onNameSearchTextChanged();
    }
  };
  const onFilterByUserNameChange = (value) => {
    setUserNameSearchText(value);
  };
  const onUserNameSearchTextChanged = () => {
    setPage(0);
    setFilterByUserName(usernameSearchText);
  };
  const onFilterByUserNameKeyPress = (e) => {
    if (e.key === KEY_ENTER) {
      onUserNameSearchTextChanged();
    }
  };
  const onFilterByEmailChange = (value) => {
    setEmailSearchText(value);
  };
  const onEmailSearchTextChanged = () => {
    setPage(0);
    setFilterByEmail(emailSearchText);
  };
  const onFilterByEmailKeyPress = (e) => {
    if (e.key === KEY_ENTER) {
      onEmailSearchTextChanged();
    }
  };
  const onFilterByOrgChange = (event) => {
    if (!event) {
      return;
    }
    const value = get(event, 'target.value', '');
    if (value) {
      props.fetchOrganizationsFiltered(50, 0, value);
    }
    // clear on selection by click
    if (get(event, "type", "") === "click") {
      setOrgSearchText('');
    } else {
      setOrgSearchText(value);
    }
  };
  const onFilterByOrganizationChange = (e, value) => {
    if (value) {
      setFilterByOrganization(get(value, 'uuid'));
    } else {
      setFilterByOrganization();
    }
    setPage(0);
    const orgName = get(e, 'target.innerText', '');
    setOrgSearchText(orgName);
    props.fetchOrganizationsFiltered(50, 0, orgName);
  };
  const onFilterByAuthSchemeChange = (authScheme) => {
    setFilterByAuthScheme(authScheme);
    setPage(0);
  };
  const onFilterByRoleChange = (newRole) => {
    setFilterByRole(newRole);
    setPage(0);
  };
  const onFilterByStatusChange = (newStatus) => {
    setFilterByStatus(newStatus);
    setPage(0);
  };
  const getFilterByExpiryStartAndEnd = (daysOfInactivity, inactivityType) =>
  {
    let startTime, endTime;
    daysOfInactivity = parseInt(daysOfInactivity);
    if(inactivityType === IS_LESS_THAN) {
      startTime = moment().subtract(daysOfInactivity, 'days').toDate();
    } else if (inactivityType === IS_GREATER_THAN) {
      endTime = moment().subtract(daysOfInactivity + 1, 'days').toDate();
    }
    return {
      startTime: startTime && encodeURI(format(startTime, SERVER_DATE_FORMAT)),
      endTime: endTime && encodeURI(format(endTime, SERVER_DATE_FORMAT)),
    };
  }
  const onFilterByInactiveDaysChange = (daysOfInactivity) => {
    if(daysOfInactivity >= 0 && daysOfInactivity !== "-") {
      setFilterByDaysOfInactivity(daysOfInactivity);
    }
  };
  const onFilterByInactivityType = (inactiveType) => {
    setFilterByInactivityType(inactiveType);
    if (filterByDaysOfInactivity) {
      const { startTime, endTime } = getFilterByExpiryStartAndEnd(filterByDaysOfInactivity,
        inactiveType);
      setFilterByLastLoginFrom(startTime);
      setFilterByLastLoginTo(endTime);
    }
    setPage(0);
  };
  const onFilterByInactiveDaysKeyPress = (e) => {
    if (e.key === '-' || e.key === '+' || e.key === '.') {
      e.preventDefault();
    }
    if (e.key === KEY_ENTER && filterByDaysOfInactivity !== appliedDaysOfInactivityFilter) {
      setAppliedDaysOfInactivityFilter(filterByDaysOfInactivity);
      const { startTime, endTime } = getFilterByExpiryStartAndEnd(filterByDaysOfInactivity,
        filterByInactivityType);
     setFilterByLastLoginFrom(startTime);
     setFilterByLastLoginTo(endTime);
    }
    setPage(0);
  };
  const onClearInactivityDays = () => {
    setFilterByDaysOfInactivity("");
    setFilterByLastLoginFrom("");
    setFilterByLastLoginTo("");
    setPage(0);
  };
  const handleBlurInactiveDays = () => {
    if(filterByDaysOfInactivity !== appliedDaysOfInactivityFilter) {
      setAppliedDaysOfInactivityFilter(filterByDaysOfInactivity);
      const { startTime, endTime } = getFilterByExpiryStartAndEnd(filterByDaysOfInactivity,
        filterByInactivityType);
      setFilterByLastLoginFrom(startTime);
      setFilterByLastLoginTo(endTime);
    }
    setPage(0);
  };
  const onSortByChange = (newSortBy) => {
    setSortBy(newSortBy);
    setPage(0);
  };
  const onChangePage = (newPage) => {
    setPage(newPage);
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };
  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const onAddUser = () => {
    window.location.href = '/admin/console/users/add';
  };

  let authSchemesOptions = map(authConfigNameMap, (value, item) => ({
    uuid: item,
    name: value,
  }));

  authSchemesOptions = [{
      uuid: 'ALL',
      name: intl.getI18nMessage('label.user.list.page.authscheme.by.all.authschemes'),
    },
    ...authSchemesOptions,
  ];

  const selectedOrgOption = find(orgOptions, item => item.uuid === filterByOrganization);
  const userRole = getUserRole(userContext);

  const isAllowedToCreateUser = () => {
    if (userRole === ROLE_PORTAL_ADMIN) {
      return true;
    } else if (userRole === ROLE_ORG_ADMIN) {
      const { authMethod, authznType } = activeUserContext;
      return (authMethod === 'DEFAULT') || (authMethod === 'SAML' && authznType === 'PORTAL');
    }
    return false;
  };

  const onBulkDelete = () => {
    if (selectedRows.length > 24)
    {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.bulk.delete.exceeds'));
    } else {
      const payload = {
        userUuids: selectedRows,
        action: USER_BULK_DELETE_REQUEST,
      }
      props.bulkDeleteUsers(payload);
    }
  }

  const { disabledRows, rows } = useMemo(() => {
    const rows = cloneDeep(results);
    const disabledRowsIds = [userContext.uuid];
    if (userRole === ROLE_ORG_ADMIN) {
      forEach(results, (item, index) => {
        const accessArray = item.access;
        const access = find(accessArray,
            accessObj => accessObj.orgUuid === userContext.organizationUuid,
          ) || {};
        if (item.isMultiOrgUser) {
          disabledRowsIds.push(item.uuid)
          rows[index].checkBoxTooltip = intl.getI18nMessage('label.user.bulk.delete.disabled.multiorg.user.tooltip');
        } else if (access.roleName === ROLE_ORG_PUBLISHER) {
          disabledRowsIds.push(item.uuid)
          rows[index].checkBoxTooltip = intl.getI18nMessage('label.user.bulk.delete.disabled.orgpublisher.user.tooltip');
        }
      });
    }
    return { disabledRows: disabledRowsIds, rows };
  }, [results, userContext, userRole]);

  return (
    <ListContainer
      userContext={userContext}
      listPageId="user-list-page"
      isLoading={isLoading}
      notificationId="user-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      pageHeaderTitle={intl.getI18nMessage('label.user.list.page.title')}
      addButtonLabel={intl.getI18nMessage('label.user.add.button')}
      onAdd={(
        isAllowedToCreateUser() && onAddUser
      )}
      disableAdd={lockUserCreation}
      filterAndSortContent={(
        <Fragment>
          <FilterByName
            fieldContainerClass={classes.nameContainer}
            name={intl.getI18nMessage('label.filter')}
            value={nameSearchText}
            placeholder={intl.getI18nMessage('label.user.list.page.filter.by.name.placeholder')}
            handleChange={onFilterByNameChange}
            onKeyPress={onFilterByNameKeyPress}
            handleBlur={onNameSearchTextChanged}
            disabled={emailSearchText.length > 0 || usernameSearchText.length > 0}
          />
          <FilterByUserName
            fieldContainerClass={classes.usernameContainer}
            name={intl.getI18nMessage('label.filter')}
            value={usernameSearchText}
            placeholder={intl.getI18nMessage('label.username')}
            handleChange={onFilterByUserNameChange}
            onKeyPress={onFilterByUserNameKeyPress}
            handleBlur={onUserNameSearchTextChanged}
            hideLabel
            disabled={emailSearchText.length > 0 || nameSearchText.length > 0}
          />
          <FilterByEmail
            fieldContainerClass={classes.emailContainer}
            name={intl.getI18nMessage('label.filter')}
            value={emailSearchText}
            placeholder={intl.getI18nMessage('label.user.list.page.filter.by.email.placeholder')}
            handleChange={onFilterByEmailChange}
            onKeyPress={onFilterByEmailKeyPress}
            handleBlur={onEmailSearchTextChanged}
            hideLabel
            disabled={nameSearchText.length > 0 || usernameSearchText.length > 0}
          />
          {isPublisher &&
            <FilterByOrganization
              filterSelectedOptions={false}
              getOptionLabel={item => (item && item.name) || ''}
              getOptionSelected={(option, value) => (value ? value.uuid === option.uuid : false)}
              hideLabel
              onInputChange={onFilterByOrgChange}
              handleChange={onFilterByOrganizationChange}
              inputLabel={intl.getI18nMessage('label.user.list.page.organization.by.all.organizations')}
              options={orgOptions}
              searchText={orgSearchText}
              value={selectedOrgOption}
              fieldContainerClass={classes.autoContainer}
            />}
          {isPublisher &&
            <FilterByAuthScheme
              data={authSchemesOptions}
              fieldContainerClass={classes.schemeContainer}
              name={intl.getI18nMessage('label.filter')}
              selectFieldClass={classes.selectField}
              value={filterByAuthScheme}
              handleChange={onFilterByAuthSchemeChange}
              hideLabel
            />
          }
          <FilterByRole
            fieldContainerClass={classes.roleContainer}
            name={intl.getI18nMessage('label.filter')}
            selectFieldClass={classes.selectField}
            value={filterByRole}
            handleChange={onFilterByRoleChange}
            hideLabel
            isPublisher={isPublisher}
          />
          <FilterByStatus
            fieldContainerClass={classes.statusContainer}
            name={intl.getI18nMessage('label.filter')}
            selectFieldClass={classes.selectField}
            value={filterByStatus}
            handleChange={onFilterByStatusChange}
            hideLabel
          />
          <FilterBySelect
              {...props}
              id="users-filter-by-daysInactive"
              data-apim-test="users-filter-by-daysInactive"
              name={intl.getI18nMessage('label.filter')}
              fieldContainerClass={classes.inactivityTypeContainer}
              selectFieldClass={classes.selectField}
              value={filterByInactivityType}
              data={inactivityFilterList}
              handleChange={onFilterByInactivityType}
              hideLabel
              noNoneOption
              noNativeSelect
          />
          <FilterByText
            {...props}
            id="days"
            data-apim-test="user-inactiveDays"
            name="Filter"
            type="number"
            fieldContainerClass={classes.inactivityDaysContainer}
            value={filterByDaysOfInactivity}
            textFieldClass={classes.textField}
            handleChange={onFilterByInactiveDaysChange}
            onKeyPress={onFilterByInactiveDaysKeyPress}
            handleBlur={handleBlurInactiveDays}
            placeholder={intl.getI18nMessage('label.user.list.page.inactive.days.placeholder')}
            hideLabel
          />
          <FilterByText
            id="inactivityDaysLabel"
            data-apim-test="inactivityDaysLabel"
            name="Filter"
            fieldContainerClass={classes.inactivityDaysLabelContainer}
            textFieldClass={classes.textField}
            placeholder={intl.getI18nMessage('label.user.list.page.inactive.days')}
            disabled={true}
            hideLabel
          />
          <Button
            id="inactivityDaysClearButton"
            variant="outlined"
            size="small"
            color="primary"
            onClick={onClearInactivityDays}
            className={classes.clearButton}
          >
            <Clear color={"primary"} />
          </Button>

          <FilterAndSortSeparator />
          <SortBy
            fieldContainerClass={classes.sortContainer}
            name={intl.getI18nMessage('label.sort')}
            selectFieldClass={classes.selectField}
            value={sortBy}
            handleChange={onSortByChange}
          />
        </Fragment>
      )}
      bulkActionsContent={
        isAllowedToCreateUser() &&
        <BulkDeleteUsers
          {...props}
          fieldContainerClass={classes.fieldContainer}
          bulkActionsItemsCount={selectedRows.length}
          onBulkDelete={ () => onBulkDelete() }
        />
      }
      columns={
        getUserListColumns(authConfigNameMap, isPublisher,
          intl.getI18nMessage, thresholdsSetting, classes)
      }
      rows={rows}
      noResultsMessage={intl.getI18nMessage('label.user.filter.no.results')}
      page={page}
      totalElements={totalElements}
      totalPages={Math.ceil(totalElements / rowsPerPage)}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      hasPaginationDisabled={selectedRows.length > 0}
      disabledRows={disabledRows}
    />
  );
};

UserList.propTypes = {
  activeUserContext: object,
  classes: object,
  userContext: object,
  isLoading: bool,
  isError: bool,
  userErrors: arrayOf(object || string),
  totalElements: number,
  results: arrayOf(object),
  lockUserCreation: bool,
  resetDeleteNotification: func,
  resetRevokeNotification: func,
  organizationsList: arrayOf(object),
  resetApp: func,
  fetchAllOrganizations: func,
  fetchRegistrationSettings: func,
  fetchUsers: func,
  fetchLockUserCreation: func,
  push: func,
  fetchOrganizationsFiltered:func,
  authSchemesList: arrayOf(object),
  registrationSettings: object,
  userDeleteResult: string,
  userRevokeAccessResult: string,
  validationError: string,
  defaultSelectedRows: arrayOf(string),
  bulkDeleteUsers: func,
  userBulkDeleteResult: string,
};

const mapStateToProps = (state) => ({
  config: getConfig(state),
  userContext: getUserDetails(state),
  isError: getIsError(state),
  userErrors: getErrors(state),
  isLoading: false,
  totalElements: getUserListTotalElements(state),
  results: getUserListResults(state),
  lockUserCreation: getLockUserCreation(state),
  organizationsList: getOrganizationListResults(state),
  registrationSettings: getRegistrationSettings(state),
  userDeleteResult: getUserDeleteResult(state),
  authSchemesList: getPublicAuthSchemesList(state),
  userRevokeAccessResult: getUserRevokeAccessResult(state),
  activeUserContext: getActiveUserContext(state),
  validationError: getValidationError(state),
  userBulkDeleteResult: getUserBulkDeleteResult(state),
});

const mapDispatchToProps = {
  push,
  resetApp,
  fetchUsers,
  fetchLockUserCreation,
  fetchRegistrationSettings,
  resetDeleteNotification,
  resetRevokeNotification,
  fetchOrganizationsFiltered,
  bulkDeleteUsers,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(UserList);
